import { useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { invoiceTotal } from "@1-now/booking-core";
import type { PayState } from "designs/Common/CheckOutPage/CheckOutPage";
import { dollars } from "./dollars";
import { deductibleLine } from "./ProductDetails";
import { orderProducts } from "./ProtectionStep";
import { getAgreementPdfBase } from "utils/agreement-url";
import { CONSENT_ID, CONSENT_REFUSAL } from "./PolicyConsent";
import { EXTRAS, PAY, PayChoice, REVIEW, Step, STEP_DEFS } from "./steps";
import type { PhoneBookingProps } from "./PhoneBooking";

/**
 * What the five steps do, the same for both layouts (1NOW-11155 on phones, 1NOW-11173 on desktop): which
 * bill is the total, what the main button says and does on each step, the agreement, the pay state, the
 * reservation (pay later). The phone's sheet and the desktop's pages only draw this.
 */
export const useStepsController = (p: PhoneBookingProps) => {
  const [pay, setPay] = useState<PayState>({ busy: false, locked: false });
  const payRef = useRef<(() => void) | null>(null);

  const bound = Boolean(p.madeBooking);
  /** The reservation, not the card payment (a car that allows pay later, and the renter pressed Pay later on the review). */
  const later = p.payLater && p.payChoice === "later";
  // At the Pay step the bill is the booking's (promo code included); before that, the quote's.
  const bill = p.step === PAY && bound ? p.bookingdetails?.invoice : p.invoice;
  // The booking's own filled-in agreement (the FMS viewer, as the old checkout opened it): the review made the
  // booking, so there is one by the time the Pay step shows the consent line.
  const agreementId = p.madeBooking?.unique_identifier;
  const agreementUrl = agreementId ? `${getAgreementPdfBase()}${agreementId}` : undefined;
  const total = invoiceTotal(bill);

  const selectedProduct = useMemo(
    () => orderProducts(p.protection.products).find((x) => JSON.stringify(x.value) === JSON.stringify(p.protection.selectedValue)),
    [p.protection.products, p.protection.selectedValue],
  );
  /** The chosen package as one line, and the add-ons and extras as one line each: what the review lists. */
  const protectionLine = useMemo(
    () =>
      selectedProduct
        ? [selectedProduct.title, deductibleLine(selectedProduct.options)].filter(Boolean).join(" · ")
        : null,
    [selectedProduct],
  );
  const extraLines = useMemo(
    () => [
      ...p.extras.addons.filter((a) => p.extras.selectedAddons.has(a.value?.[0])).map((a) => a.title),
      ...p.extras.extras.filter((x) => p.extras.selectedExtras.has(x.id)).map((x) => x.name),
    ],
    [p.extras],
  );

  // Sent here from the review's Edit: the step runs its own check and goes straight back to the review.
  const back = p.returning && p.step < REVIEW;

  // 1NOW-11218: a car with no extras and no add-ons has no Extras step. The page empties the add-ons while
  // Bonzah answers, so the lists count only once it has: Continue still goes to Extras while they load.
  const noExtras = p.extras.extras.length === 0 && p.extras.addons.length === 0;
  const skipExtras = noExtras && !p.protection.loading;
  // The bar keeps the last loaded answer, so picking another package (a reload) does not flash Extras in and out.
  const settled = useRef(false);
  if (!p.protection.loading) settled.current = noExtras;
  /** Extras is left out of the bar, the Back and the review's Edit; never while the renter is on it. */
  const hideExtras = settled.current && p.step !== EXTRAS;
  /** The steps the bar shows, each with its number in the flow (the address's). */
  const shownSteps = STEP_DEFS.map((d, i) => ({ ...d, step: (i + 1) as Step })).filter(
    (d) => !(hideExtras && d.step === EXTRAS),
  );
  const prevStep = (p.step === REVIEW && hideExtras ? EXTRAS - 1 : p.step - 1) as Step;

  /**
   * The review's buttons: "Pay and book now" (the card booking) and, for a car that allows pay later, "Pay later"
   * (the reservation). Each is the answer the review's radios used to give, then what "Continue to book" did: the
   * page's checks, the booking made (or kept), and Pay.
   */
  const book = (how: PayChoice) => {
    if (p.checkDetails()) p.onContinueFromReview(how);
  };
  const payLaterButton =
    p.step === REVIEW && p.payLater
      ? { label: "Pay later", onClick: () => book("later"), busy: p.busyLater, disabled: p.busy }
      : undefined;

  const next = () => {
    if (p.step === 1) {
      if (p.checkDetails()) p.setStep(back ? 4 : 2);
    } else if (p.step === 2) {
      if (p.checkProtection()) p.setStep(back || skipExtras ? REVIEW : EXTRAS);
    } else if (p.step === EXTRAS) {
      p.setStep(REVIEW);
    } else if (p.step === REVIEW) {
      book("now");
    }
  };

  const onBar = () => {
    if (p.step < PAY) return next();
    // Pay (card) and Reserve (pay later) are both for a renter who accepted the agreement and policies: the one
    // consent line, above the button. Refused, it is the line the renter is taken to.
    if (!p.accepted) {
      toast.error(CONSENT_REFUSAL);
      document.getElementById(CONSENT_ID)?.scrollIntoView?.({ behavior: "smooth", block: "center" });
      return;
    }
    // A reservation is already made (it is the booking whose agreement was accepted): the renter goes to its page.
    if (later) p.onReserve();
    else payRef.current?.();
  };

  const barLabel =
    p.step < REVIEW
      ? back
        ? "Back to review"
        : "Continue"
      : p.step === REVIEW
        ? "Pay and book now"
        : later
          ? "Reserve"
          : pay.locked
            ? "Payment not confirmed yet"
            : pay.depositNow
              ? `Pay ${dollars(pay.depositNow)} and book`
              : "Pay and book";

  return {
    agreementUrl,
    later,
    pay,
    setPay,
    payRef,
    bound,
    bill,
    total,
    protectionLine,
    extraLines,
    hideExtras,
    shownSteps,
    prevStep,
    onBar,
    barLabel,
    /** The review's second button, beside the bar's: none on other steps, or for a car that is card only. */
    payLaterButton,
    /** The bar's button is busy: the card payment, or the booking (card or reservation) being made. */
    busy: p.step === PAY && bound && !later ? pay.busy : p.step === REVIEW && p.payLater ? p.busy : p.busy || p.busyLater,
    disabled: (p.step === PAY && bound && !later && pay.locked) || Boolean(payLaterButton?.busy),
  };
};
